import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import PaymentForm from './PaymentForm';

interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
  totalDueAmount?: number;
}

interface UserInfo {
  name: string;
  gn: string;
}

interface PaymentAccordionProps {
  payments: Payment[];
  userInfo: UserInfo;
  expandedPaymentId: string | null;
  onPaymentSelect: (payment: Payment) => void;
  onHideDetails: (e: React.MouseEvent) => void;
  isScheduledPayment: boolean;
  setIsScheduledPayment: (value: boolean) => void;
  scheduleFrequency: string;
  setScheduleFrequency: (value: string) => void;
  splitType: string;
  setSplitType: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  numberOfPayments: number;
  setNumberOfPayments: (value: number) => void;
  amountPerPayment: string;
  setAmountPerPayment: (value: string) => void;
  endDate: string;
  isScheduledPaymentValid: () => boolean;
  loadPayabliScript: (payment: any) => void;
  getMinStartDate: () => string;
  SCHEDULE_FREQUENCIES: any;
  SPLIT_TYPES: any;
}

const PaymentAccordion: React.FC<PaymentAccordionProps> = ({
  payments,
  userInfo,
  expandedPaymentId,
  onPaymentSelect,
  onHideDetails,
  isScheduledPayment,
  setIsScheduledPayment,
  scheduleFrequency,
  setScheduleFrequency,
  splitType,
  setSplitType,
  startDate,
  setStartDate,
  numberOfPayments,
  setNumberOfPayments,
  amountPerPayment,
  setAmountPerPayment,
  endDate,
  isScheduledPaymentValid,
  loadPayabliScript,
  getMinStartDate,
  SCHEDULE_FREQUENCIES,
  SPLIT_TYPES
}) => {
  const { theme } = useTheme();

  return (
    <div className="space-y-4 mt-4">
      {payments.map(payment => (
        <div key={payment.id} className="bg-white rounded-xl shadow-lg overflow-hidden card-hover">
          <div 
            className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={(e) => {
              if (expandedPaymentId === payment.id) {
                onHideDetails(e);
              } else {
                onPaymentSelect(payment); 
              }
            }}
          >
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2 justify-between">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {payment.invoiceNumber ? `Invoice #${payment.invoiceNumber}` : `Payment #${payment.id}`}
                      </h3>
                      <p className="text-lg font-bold text-gray-900">Pending: ${payment.totalDueAmount || 0}</p>
                    </div>
                    <div className="ml-4 flex items-center">
                      <svg 
                        className={`ml-2 w-5 h-5 transition-transform duration-300 ${
                          expandedPaymentId === payment.id ? 'rotate-180' : ''
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        onClick={e => { 
                          e.stopPropagation(); 
                          if (expandedPaymentId === payment.id) {
                            onHideDetails(e);
                          } else {
                            onPaymentSelect(payment); 
                          }
                        }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <p><span className="font-medium text-gray-700">Statment Date:</span> {new Date(payment.statementDate).toLocaleDateString()}</p>
                      <p><span className="font-medium text-gray-700">Due:</span> {new Date(payment.dueDate).toLocaleDateString()}</p>
                      {/* <p><span className="font-medium text-gray-700">Type:</span> {payment.paymentType || 'N/A'}</p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {expandedPaymentId === payment.id && (
            <div className="border-t border-gray-200 p-6 accordion-expand">
              <PaymentForm
                payment={payment}
                userInfo={userInfo}
                isScheduledPayment={isScheduledPayment}
                setIsScheduledPayment={setIsScheduledPayment}
                scheduleFrequency={scheduleFrequency}
                setScheduleFrequency={setScheduleFrequency}
                splitType={splitType}
                setSplitType={setSplitType}
                startDate={startDate}
                setStartDate={setStartDate}
                numberOfPayments={numberOfPayments}
                setNumberOfPayments={setNumberOfPayments}
                amountPerPayment={amountPerPayment}
                setAmountPerPayment={setAmountPerPayment}
                endDate={endDate}
                isScheduledPaymentValid={isScheduledPaymentValid}
                loadPayabliScript={(paymentObj) => loadPayabliScript(paymentObj)}
                getMinStartDate={getMinStartDate}
                SCHEDULE_FREQUENCIES={SCHEDULE_FREQUENCIES}
                SPLIT_TYPES={SPLIT_TYPES}
                isPayButtonVisible={false}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PaymentAccordion; 